import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import Logo from "./Logo";
import { useLang, LanguageSwitcher } from "../i18n";

const Header = () => {
    const [open, setOpen] = useState(false);
    const { pathname } = useLocation();
    const { t } = useLang();

    const links = [
        { to: "/servicios", label: t.nav.servicios },
        { to: "/sobre-nosotros", label: t.nav.sobre },
        { to: "/zona-de-servicio", label: t.nav.zona },
        { to: "/faq", label: t.nav.faq },
        { to: "/contacto", label: t.nav.contacto },
    ];

    return (
        <header
            data-testid="nav-header"
            className="sticky top-0 z-50 border-b border-[#E3DCCF]/80 bg-cream/85 backdrop-blur-md"
        >
            <div className="mx-auto flex max-w-7xl items-center justify-between gap-3 px-5 py-3.5 sm:px-8">
                <Link to="/" aria-label="Maresme Dog Care" onClick={() => setOpen(false)}>
                    <Logo compact />
                </Link>

                <nav className="hidden items-center gap-7 lg:flex" aria-label="Navegación principal">
                    {links.map((l) => (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            data-testid={`nav-link-${l.to.replace("/", "")}`}
                            className={({ isActive }) =>
                                `text-[14px] font-semibold transition-colors duration-200 hover:text-terracotta ${
                                    isActive ? "text-terracotta" : "text-[#525B53]"
                                }`
                            }
                        >
                            {l.label}
                        </NavLink>
                    ))}
                </nav>

                <div className="flex items-center gap-3">
                    <LanguageSwitcher className="hidden sm:flex" />
                    <Link
                        to="/contacto"
                        data-testid="nav-cta-button"
                        className="hidden rounded-full bg-terracotta px-5 py-2.5 text-[14px] font-bold text-cream shadow-sm transition-[background-color,transform] duration-200 hover:bg-terracotta-dark hover:-translate-y-0.5 md:inline-block"
                    >
                        {t.nav.reserva}
                    </Link>
                    <button
                        data-testid="nav-menu-toggle"
                        className="rounded-lg p-2 text-ink transition-colors hover:bg-sand/60 lg:hidden"
                        onClick={() => setOpen(!open)}
                        aria-label={open ? t.nav.closeMenu : t.nav.openMenu}
                        aria-expanded={open}
                    >
                        {open ? <X size={22} /> : <Menu size={22} />}
                    </button>
                </div>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.nav
                        key={pathname + "-mobile"}
                        data-testid="nav-mobile-menu"
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                        className="overflow-hidden border-t border-[#E3DCCF] bg-cream lg:hidden"
                    >
                        <div className="flex flex-col gap-1 px-5 py-4">
                            {links.map((l) => (
                                <NavLink
                                    key={l.to}
                                    to={l.to}
                                    onClick={() => setOpen(false)}
                                    className={({ isActive }) =>
                                        `rounded-xl px-4 py-3 text-[15px] font-semibold transition-colors ${
                                            isActive ? "bg-olive-light text-olive-dark" : "text-[#525B53] hover:bg-sand/50"
                                        }`
                                    }
                                >
                                    {l.label}
                                </NavLink>
                            ))}
                            <Link
                                to="/contacto"
                                onClick={() => setOpen(false)}
                                data-testid="nav-cta-mobile"
                                className="mt-2 rounded-full bg-terracotta px-5 py-3 text-center text-[15px] font-bold text-cream"
                            >
                                {t.nav.reserva}
                            </Link>
                            <div className="mt-3 flex justify-center sm:hidden">
                                <LanguageSwitcher />
                            </div>
                        </div>
                    </motion.nav>
                )}
            </AnimatePresence>
        </header>
    );
};

export default Header;
